<template>
    <div>
        <div v-if="loading">
            <loading />
        </div>
        <div v-else>
            <div class="row">
                <div class="col-3">
                    <b-form-group>
                        <label>SIREN / SIRET</label>
                        <b-input
                            v-model="form.siren"
                            :formatter="sirenFormatter"
                            :disabled="this.ppOption.value == this.form.legal_form_id"
                            @input="showNotSaved"
                        />
                    </b-form-group>
                </div>
                <div class="col-2">
                    <b-form-group>
                        <label>Forme juridique</label>
                        <b-form-select
                            v-model="form.legal_form_id"
                            :options="legalFormOptions"
                            @change="showNotSaved"
                        />
                    </b-form-group>
                </div>
                <div class="col-4">
                    <b-form-group>
                        <label>Raison sociale</label>
                        <b-input v-model="form.name" @input="showNotSaved" />
                    </b-form-group>
                </div>
                <div class="col-3">
                    <b-form-group>
                        <label>Groupe</label>
                        <b-form-select
                            v-model="form.client_group_id"
                            :options="activeClientGroupOptions"
                            @change="showNotSaved"
                        />
                    </b-form-group>
                </div>
            </div>

            <div class="row">
                <div class="col-9">
                    <b-form-group>
                        <label>Adresse</label>
                        <b-input
                            v-model="form.formatted_address"
                            @input="showNotSaved"
                        />
                    </b-form-group>
                </div>
                <div class="col-3">
                    <b-form-group>
                        <label>Greffe</label>
                        <b-input
                            v-model="form.registration_city"
                            :disabled="company.legal_form === 'PP'"
                            @input="showNotSaved"
                        />
                    </b-form-group>
                </div>
            </div>

            <div class="row">
                <div class="col-4">
                    <label>Création de l'activité</label>
                    <b-input-group class="mb-3">
                        <b-form-input
                            id="activity-start"
                            v-model="formatted_activity_start"
                            type="text"
                            placeholder="Non définie"
                            autocomplete="off"
                            disabled
                            class="bg-white"
                        />
                        <b-input-group-append>
                            <b-form-datepicker
                                v-model="form.activity_start"
                                button-only
                                right
                                reset-button
                                close-button
                                label-close-button="Fermer"
                                label-reset-button="Effacer"
                                label-no-date-selected="Non définie"
                                label-prev-year="Année précédente"
                                label-next-year="Année prochaine"
                                label-prev-month="Mois précédent"
                                label-next-month="Mois prochain"
                                label-current-month="Mois actuel"
                                label-help="Vous pouvez utiliser les flèches du clavier pour naviguer"
                                start-weekday="1"
                                aria-controls="activity-start"
                                :date-format-options="{
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                }"
                                locale="fr"
                                :disabled="company.legal_form === 'PP'"
                                @context="formatActivityStart"
                                @input="showNotSaved"
                            >
                            </b-form-datepicker>
                        </b-input-group-append>
                    </b-input-group>
                </div>

                <div class="col-4">
                    <label>Clôture premier exercice</label>
                    <b-input-group class="mb-3">
                        <b-form-input
                            id="first-end-of-exercise"
                            v-model="formatted_first_end_of_exercise"
                            type="text"
                            placeholder="Non définie"
                            autocomplete="off"
                            disabled
                            class="bg-white"
                        />
                        <b-input-group-append>
                            <b-form-datepicker
                                v-model="form.first_end_of_exercise"
                                button-only
                                right
                                reset-button
                                close-button
                                label-close-button="Fermer"
                                label-reset-button="Effacer"
                                label-no-date-selected="Non définie"
                                label-prev-year="Année précédente"
                                label-next-year="Année prochaine"
                                label-prev-month="Mois précédent"
                                label-next-month="Mois prochain"
                                label-current-month="Mois actuel"
                                label-help="Vous pouvez utiliser les flèches du clavier pour naviguer"
                                start-weekday="1"
                                aria-controls="first-end-of-exercise"
                                :date-format-options="{
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                }"
                                locale="fr"
                                :disabled="company.legal_form === 'PP'"
                                @context="formatFirstEndOfExercise"
                                @input="showNotSaved"
                            >
                            </b-form-datepicker>
                        </b-input-group-append>
                    </b-input-group>
                </div>
                <div class="col-4">
                    <b-form-group>
                        <label>Clôture de l'exercice</label>
                        <b-form-select
                            v-model="form.end_of_exercise"
                            :options="endOfExerciseOptions"
                            :disabled="company.legal_form === 'PP'"
                            @change="showNotSaved"
                        />
                    </b-form-group>
                </div>
            </div>
            <b-alert variant="success" :show="showSavedAlert">
                Les modifications ont bien été enregistrées
            </b-alert>
            <b-alert variant="danger" :show="showNotSavedAlert">
                Les modifications ne sont pas enregistrées
            </b-alert>
            <b-alert variant="danger" :show="showErrorAlert">
                {{ errorMessage }}
            </b-alert>
            <b-button
                variant="success"
                class="btn-success"
                title="Enregister"
                @click="legalDataSave"
            >
                <i class="fal fa-save fa-fw" /><span class="pl-2">Enregistrer</span>
            </b-button>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'

import Formatter from '../../../../common/mixins/Formatter'
import Loading from '../../../../common/components/loading'

export default {
    name: 'ClientLegalForm',

    components: {
        Loading
    },

    mixins: [Formatter],

    props: {
        company: null
    },

    data() {
        return {
            form: {},
            formatted_activity_start: '',
            formatted_first_end_of_exercise: '',
            showSavedAlert: false,
            showNotSavedAlert: false,
            showErrorAlert: false,
            errorMessage: ''
        }
    },

    computed: {
        ...mapState({
            loading: (state) => state.browsing.loading,
            key: (state) => state.browsing.key,
            legalFormOptions: (state) => state.data.options.legalForms,
            endOfExerciseOptions: (state) => state.data.options.endsOfExercise,
            clientGroupOptions: (state) => state.data.options.clientGroups
        }),

        activeClientGroupOptions() {
            return this.clientGroupOptions
                ? this.clientGroupOptions.filter(item => item.value !== 0)
                : []
        },

        ppOption() {
            return this.legalFormOptions.find(item => item.text === 'PP')
        }

    },

    async created() {
        // this.$store.commit('browsing/START_LOADING')
        // await this.$store.dispatch('legalVM/getClientData', this.company.id)
        this.initForm()
        // this.$store.commit('browsing/STOP_LOADING')
    },

    methods: {
        showNotSaved() {
            this.showSavedAlert = false
            this.showNotSavedAlert = true
            this.showErrorAlert = false
        },
        showSaved() {
            this.showSavedAlert = true
            this.showNotSavedAlert = false
            this.showErrorAlert = false
        },
        showError() {
            this.showSavedAlert = false
            this.showNotSavedAlert = false
            this.showErrorAlert = true
        },

        initForm() {
            this.form.siren = this.sirenFormatter(this.company.siret)
            this.form.end_of_exercise =
                this.company.end_of_exercise === null
                    ? 0
                    : this.company.end_of_exercise
            this.form.legal_form_id = this.company.legal_form_id
            this.form.name = this.company.name
            this.form.client_group_id = this.company.client_group_id
            this.form.formatted_address = this.company.formatted_address
            this.form.registration_city = this.company.registration_city
            this.form.activity_start = this.company.activity_start
            this.form.first_end_of_exercise = this.company.first_end_of_exercise
        },

        async legalDataSave() {
            try {
                await axios.put(`clients/${this.company.id}/legal`, this.form)
                this.showSaved()
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 422) {
                        this.errorMessage = error.response.data.meta.message
                        this.showError()
                    }
                }
            }
        },

        formatActivityStart(ctx) {
            this.formatted_activity_start = ctx.selectedFormatted
        },

        formatFirstEndOfExercise(ctx) {
            this.formatted_first_end_of_exercise = ctx.selectedFormatted
        }
    }


}
</script>

<style scoped></style>
